import axios from "axios"
import {useCompany} from "./useCompany"

// Loads Area > Zone > Point trees for the point pickers. The backend decides
// what the user may see (allowed zones, no default area, no diagnostic
// points), so callers only say which site or sites.
export function usePointTree() {
    const {buildCompanyApiInternalRoute} = useCompany()

    // Another site is requested through the site the page is on
    // (`point-tree/{site}`), never as the URL's own site: the backend saves the
    // URL's site as the session's current site, and permission checks fall back
    // to it. A site the user cannot access on the plain route is answered with
    // a redirect to an HTML error page, which the browser follows and axios
    // reports as a 200. So the body's shape is checked, not only the status.
    // `withSerial` adds each point's device serial, for callers that count devices.
    const fetchSiteTree = (siteId = null, {withSerial = false} = {}) => axios
        .get(buildCompanyApiInternalRoute(siteId === null ? 'point-tree' : `point-tree/${siteId}`), {
            params: withSerial ? {with: 'serial'} : {},
        })
        .then((response) => {
            if (! Array.isArray(response.data?.areas)) {
                throw new Error(`No point tree returned for ${siteId ?? 'the current site'}`)
            }

            return response.data.areas
        })

    // One request per site, at most `concurrency` at a time. Each site reports
    // as soon as it returns, so the tree fills in progressively and one failed
    // site does not stop the others.
    const loadSites = async (siteIds, onResult, concurrency = 4) => {
        const queue = [...siteIds]
        const worker = async () => {
            while (queue.length > 0) {
                const siteId = queue.shift()
                try {
                    onResult(siteId, await fetchSiteTree(siteId), null)
                } catch (error) {
                    onResult(siteId, null, error)
                }
            }
        }

        await Promise.all(Array.from({length: Math.min(concurrency, queue.length)}, worker))
    }

    return {fetchSiteTree, loadSites}
}

// Area > Zone > Point to TreeSelect nodes. `prefix` keeps branch keys unique
// when several sites share one tree; `leafKey` decides what a selected point
// looks like to the caller.
export function areasToNodes(areas, prefix, leafKey) {
    return areas.map((area) => ({
        key: `${prefix}a:${area.id}`,
        label: area.name,
        children: area.zones.map((zone) => ({
            key: `${prefix}z:${zone.id}`,
            label: zone.name,
            children: zone.meters.map((meter) => ({key: leafKey(meter), label: meter.name, meter})),
        })),
    }))
}
